<script setup lang="ts">
/**
 * "Виды" pane of the Services tab: service types grouped under their
 * categories (this replaces the former standalone Categories and Types tabs).
 *
 * Infinite-scrolls `/business-service-types` (grouped by category by the API)
 * and splices a synthetic `category-header` row ahead of each category's types
 * via `rowOverride` (`CategoryHeaderRowCell`: category name, number of types,
 * "+" to add a type to it). Categories without any type still get a band, so
 * they can be opened and edited. Clicking a band opens the category card;
 * clicking a type opens `BusinessServiceTypeCard`. Layout follows
 * `docs/sketches/desktop/service-types-table.html` (left pane) and
 * `docs/sketches/mobile/service-types-table.html`.
 */
import { computed, markRaw, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import RowIndexCell from '@/components/table/cell/RowIndexCell.vue'
import TwoLineTextCell from '@/components/table/cell/TwoLineTextCell.vue'
import MoneyCell from '@/components/table/cell/MoneyCell.vue'
import CategoryHeaderRowCell from '@/components/table/cell/CategoryHeaderRowCell.vue'
import ServiceTypeMobileLeftCell from '@/components/table/cell/ServiceTypeMobileLeftCell.vue'
import ServiceTypeMobileRightCell from '@/components/table/cell/ServiceTypeMobileRightCell.vue'
import ServiceTypeMobileScreen from '@/components/main/businessServiceTypeCard/ServiceTypeMobileScreen.vue'
import ServiceCategoryMobileScreen from '@/components/main/serviceCategoryCard/ServiceCategoryMobileScreen.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import { usePermissions } from '@/composables/usePermissions'
import { useDrainPages } from '@/composables/useDrainPages'
import {
  businessServiceTypePriceAmount,
  deleteBusinessServiceType,
  fetchBusinessServiceTypes,
  type BusinessServiceType,
} from '@/api/businessServiceTypes'
import { fetchBusinessServiceCategories } from '@/api/businessServiceCategories'
import { formatDuration } from '@/utils/formatDuration'
import { moneyColumnLabel } from '@/utils/formatMoney'
import { serviceTypesPollingInterval, serviceCategoriesPollingInterval } from '@/config/api'
import { useBusinessServiceTypeCardStore } from '@/stores/business_service_type_card_store'
import { useBusinessServiceNameCardStore } from '@/stores/business_service_name_card_store'
import { useAddServiceTypeFormStore } from '@/stores/add_service_type_form_store'
import { clearInfoViewSelections } from '@/stores/info_view_selection'
import { ResourceAccessRole } from '@/models/resource_access_role'

const businessServiceTypeCardStore = useBusinessServiceTypeCardStore()
const businessServiceNameCardStore = useBusinessServiceNameCardStore()
const addServiceTypeFormStore = useAddServiceTypeFormStore()
const { isMobile } = useIsMobile()
const { t } = useI18n()
const { hasRole } = usePermissions()

interface ServiceTypeRow {
  // Row for the service types table, flattened from grouped GET `/business-service-types`.

  kind: 'type'
  id: string
  // Service type id (UUID7); powers row selection and the detail card endpoint.

  rowNumber: number
  // 1-based index over all types, continuing across categories.

  nameUpper?: string
  // Service type description; `undefined` when blank.

  nameLower: string
  // `<category> <duration>` (e.g. `бачата 1ч`), muted and smaller.

  price?: number
  // Raw price in major units; `undefined` when the API returned `null`.

  _categoryId: string
  // Parent category id (UUID7); needed by the edit handler.

  _raw: BusinessServiceType
  // Full API type record; used by the row edit handler to pre-fill the form.
}

interface CategoryHeaderRow {
  // Synthetic band spliced in ahead of each category's types; rendered by
  // `CategoryHeaderRowCell` via `pickRowOverride`.

  kind: 'category-header'
  id: string
  name: string
  count: number
  onOpen: () => void
}

type ServiceTypeListItem = ServiceTypeRow | CategoryHeaderRow

// Typed over `ServiceTypeListItem` and each props() casts its row, because
// the data also holds category bands; `rowOverride` guarantees props() is
// never invoked for those.
const columns = computed<Column<ServiceTypeListItem>[]>(() => [
  {
    key: 'rowNumber',
    label: '#',
    component: markRaw(RowIndexCell),
    props: (row) => ({ index: (row as ServiceTypeRow).rowNumber }),
    width: '3rem',
    align: 'center',
  },
  {
    key: 'name',
    label: t('common.name'),
    component: markRaw(TwoLineTextCell),
    props: (row) => {
      const r = row as ServiceTypeRow
      return { title: r.nameUpper, subtitle: r.nameLower }
    },
  },
  {
    key: 'price',
    label: moneyColumnLabel(t('common.price')),
    component: markRaw(MoneyCell),
    props: (row) => ({
      amount: (row as ServiceTypeRow).price,
      precision: 0,
      fractionDigits: 2,
      emptyPlaceholder: '–',
    }),
    width: '10rem',
    align: 'center',
  },
])

const mobileColumns: Column<ServiceTypeListItem>[] = [
  {
    key: 'nameUpper',
    label: '',
    component: markRaw(ServiceTypeMobileLeftCell),
    props: (row) => {
      const r = row as ServiceTypeRow
      return { index: r.rowNumber, nameUpper: r.nameUpper, nameLower: r.nameLower }
    },
  },
  {
    key: 'price',
    label: '',
    component: markRaw(ServiceTypeMobileRightCell),
    props: (row) => ({ price: (row as ServiceTypeRow).price }),
    width: '6rem',
    align: 'right',
  },
]

const {
  data: queryData,
  isPending,
  isError,
  error,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ['businessServiceTypes'],
  queryFn: ({ pageParam }) => fetchBusinessServiceTypes(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  refetchInterval: serviceTypesPollingInterval,
  refetchIntervalInBackground: false,
})

/**
 * Categories are drained completely: they supply the band names and the
 * (possibly empty) categories that have no types yet. Shares the
 * `['businessServiceCategories']` cache with the service forms. A viewer
 * without the categories role still sees the types, under `Category #<id>`
 * bands built from the type groups alone.
 */
const categoriesQuery = useInfiniteQuery({
  queryKey: ['businessServiceCategories'],
  queryFn: ({ pageParam }) => fetchBusinessServiceCategories(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  refetchInterval: serviceCategoriesPollingInterval,
  refetchIntervalInBackground: false,
  enabled: computed(() => hasRole(ResourceAccessRole.BusinessServiceNamesView)),
})
useDrainPages(categoriesQuery)

function openCategory(categoryId: string): void {
  clearInfoViewSelections('businessServiceName')
  businessServiceNameCardStore.selectServiceCategory(categoryId)
}

const groupedData = computed<ServiceTypeListItem[]>(() => {
  const typesByCategory = new Map<string, BusinessServiceType[]>()
  for (const page of queryData.value?.pages ?? []) {
    for (const group of page.items ?? []) {
      const list = typesByCategory.get(group.business_service_name_id) ?? []
      list.push(...(group.business_service_types ?? []))
      typesByCategory.set(group.business_service_name_id, list)
    }
  }

  const categories = new Map<string, string>()
  for (const page of categoriesQuery.data.value?.pages ?? []) {
    for (const item of page.items ?? []) categories.set(item.id, item.name)
  }
  for (const categoryId of typesByCategory.keys()) {
    if (!categories.has(categoryId)) {
      categories.set(categoryId, t('tabs.services.categoryFallback', { id: categoryId }))
    }
  }

  const ordered = [...categories.entries()].sort(([, a], [, b]) => a.localeCompare(b, 'ru'))
  const result: ServiceTypeListItem[] = []
  let index = 0
  for (const [categoryId, categoryName] of ordered) {
    const types = typesByCategory.get(categoryId) ?? []
    result.push({
      kind: 'category-header',
      id: `category-${categoryId}`,
      name: categoryName,
      count: types.length,
      onOpen: () => openCategory(categoryId),
    })
    for (const svcType of types) {
      index += 1
      const description = svcType.description
      const duration = formatDuration(svcType.duration) ?? undefined
      result.push({
        kind: 'type',
        id: svcType.id,
        rowNumber: index,
        nameUpper:
          typeof description === 'string' && description.trim() !== ''
            ? description.trim()
            : undefined,
        nameLower: [categoryName, duration].filter(Boolean).join(' '),
        price: businessServiceTypePriceAmount(svcType),
        _categoryId: categoryId,
        _raw: svcType,
      })
    }
  }
  return result
})

const typeCount = computed(() => groupedData.value.filter((i) => i.kind === 'type').length)
const categoryCount = computed(
  () => groupedData.value.filter((i) => i.kind === 'category-header').length,
)

/** Selects `CategoryHeaderRowCell` for synthetic category bands. */
function pickRowOverride(row: unknown): Component | null {
  if ((row as ServiceTypeListItem).kind === 'category-header') {
    return markRaw(CategoryHeaderRowCell)
  }
  return null
}

function rowKeyFn(row: unknown): string {
  return (row as ServiceTypeListItem).id
}

function onRowClick(row: unknown) {
  clearInfoViewSelections('businessServiceType')
  businessServiceTypeCardStore.selectBusinessServiceType((row as ServiceTypeRow).id)
}

const selectedRowKey = computed<string | null>(
  () => businessServiceTypeCardStore.selectedBusinessServiceTypeId,
)

const queryClient = useQueryClient()
const deleteMutation = useMutation({ mutationFn: (id: string) => deleteBusinessServiceType(id) })

function onRowDeleteHandler(row: unknown): void {
  const r = row as ServiceTypeRow
  const label = r.nameUpper ?? r.nameLower
  if (!confirm(t('tabs.serviceTypes.confirmDelete', { name: label }))) return
  deleteMutation.mutate(r.id, {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['businessServiceTypes'] })
      if (businessServiceTypeCardStore.selectedBusinessServiceTypeId === r.id)
        businessServiceTypeCardStore.clearSelection()
    },
  })
}

function onRowEditHandler(row: unknown): void {
  const r = row as ServiceTypeRow
  clearInfoViewSelections('addServiceTypeForm')
  addServiceTypeFormStore.openForEdit(r._raw.id, {
    serviceCategoryId: r._categoryId,
    description: r._raw.description,
    duration: r._raw.duration,
    price: r._raw.price?.price ?? null,
    priceStart: r._raw.price?.start ?? '',
  })
}
</script>

<template>
  <p v-if="isPending" class="text-tufte-muted">{{ t('tabs.serviceTypes.loading') }}</p>
  <p v-else-if="isError" class="text-red-600">
    {{
      t('tabs.serviceTypes.loadError', {
        message: (error as Error)?.message ?? t('common.unknownError'),
      })
    }}
  </p>
  <template v-else>
    <p class="service-types-summary">
      {{ t('units.serviceType', typeCount) }} · {{ t('units.category', categoryCount) }}
    </p>
    <!-- pinnable-header is always false: sticky category bands and a pinned
         <thead> both target `top: 0` on the same scroll container and would
         collide (same trade-off as the Services pane's date headers). -->
    <DynamicTable
      :class="isMobile ? 'service-types-table--mobile' : 'service-types-table'"
      :columns="isMobile ? (mobileColumns as Column<unknown>[]) : (columns as Column<unknown>[])"
      :data="groupedData"
      :has-next-page="hasNextPage"
      :is-fetching-next-page="isFetchingNextPage"
      :on-load-more="fetchNextPage"
      :on-row-click="onRowClick"
      :row-override="pickRowOverride"
      :row-key="rowKeyFn"
      :selected-row-key="isMobile ? null : selectedRowKey"
      :on-row-edit="
        isMobile || !hasRole(ResourceAccessRole.BusinessServiceTypesEdit)
          ? undefined
          : onRowEditHandler
      "
      :on-row-delete="
        isMobile || !hasRole(ResourceAccessRole.BusinessServiceTypesDelete)
          ? undefined
          : onRowDeleteHandler
      "
      :pinnable-header="false"
      storage-key="service-types"
    />
  </template>

  <ServiceTypeMobileScreen
    v-if="isMobile && businessServiceTypeCardStore.hasSelection"
    :service-type-id="businessServiceTypeCardStore.selectedBusinessServiceTypeId!"
    @close="businessServiceTypeCardStore.clearSelection()"
  />
  <ServiceCategoryMobileScreen
    v-if="isMobile && businessServiceNameCardStore.hasSelection"
    :category-id="businessServiceNameCardStore.selectedBusinessServiceNameId!"
    @close="businessServiceNameCardStore.clearSelection()"
  />
</template>

<style scoped>
.service-types-summary {
  margin: 0 0 0.5rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
}

/* Desktop: fill the whole info view so the add button sits over the table's right edge; the name column takes the leftover width. */
.service-types-table :deep(.dynamic-table) {
  width: 100%;
}

/* Mobile table: full-width, no thead, tighter horizontal padding. */
.service-types-table--mobile :deep(.dynamic-table) {
  width: 100%;
}

.service-types-table--mobile :deep(thead) {
  display: none;
}

.service-types-table--mobile :deep(td) {
  padding: 0.8rem 1.25rem;
}
</style>
